import React from 'react';
import { Globe, RotateCcw, RotateCw, Languages, Printer } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';

/**
 * Print options modal (orientation + language + bilingual) — extracted from DetailedReports.js.
 * Controlled component: parent owns all state.
 */
const PrintOptionsModal = ({
  open,
  isArabic,
  orientation,
  onOrientationChange,
  language,
  onLanguageChange,
  bilingual,
  onBilingualChange,
  onConfirm,
  onClose,
}) => {
  if (!open) return null;

  const orientBtnCls = (active) =>
    `px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1 ${
      active ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'
    }`;
  const langBtnCls = (active) =>
    `px-3 py-1.5 text-sm rounded-md transition-colors ${
      active ? 'bg-primary text-primary-foreground' : 'bg-secondary hover:bg-secondary/80'
    }`;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="print-options-modal">
      <div className="bg-card p-6 rounded-xl max-w-sm w-full mx-4 space-y-4">
        <div className="flex items-center gap-2 text-lg font-bold">
          <Globe className="w-5 h-5 text-primary" />
          {isArabic ? 'خيارات الطباعة' : 'Print Options'}
        </div>

        {/* Orientation Selection */}
        <div className="space-y-3 border border-white/10 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {isArabic ? 'اتجاه الصفحة' : 'Page Orientation'}
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => onOrientationChange('portrait')}
                className={orientBtnCls(orientation === 'portrait')}
                data-testid="print-orient-portrait"
              >
                <RotateCcw className="w-3 h-3" />
                {isArabic ? 'عمودي' : 'Portrait'}
              </button>
              <button
                type="button"
                onClick={() => onOrientationChange('landscape')}
                className={orientBtnCls(orientation === 'landscape')}
                data-testid="print-orient-landscape"
              >
                <RotateCw className="w-3 h-3" />
                {isArabic ? 'أفقي' : 'Landscape'}
              </button>
            </div>
          </div>
        </div>

        {/* Language Selection */}
        <div className="space-y-3 border border-white/10 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {isArabic ? 'لغة التقرير' : 'Report Language'}
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => onLanguageChange('ar')}
                className={langBtnCls(language === 'ar')}
                data-testid="print-lang-ar"
              >
                عربي
              </button>
              <button
                type="button"
                onClick={() => onLanguageChange('en')}
                className={langBtnCls(language === 'en')}
                data-testid="print-lang-en"
              >
                English
              </button>
            </div>
          </div>

          {/* Bilingual Toggle */}
          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            <div className="flex items-center gap-2">
              <Languages className="w-4 h-4 text-blue-500" />
              <span className="text-sm">{isArabic ? 'ثنائي اللغة' : 'Bilingual'}</span>
            </div>
            <Switch
              checked={bilingual}
              onCheckedChange={onBilingualChange}
              data-testid="print-bilingual-toggle"
            />
          </div>
        </div>

        <div className="flex gap-3">
          <Button onClick={onConfirm} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="print-confirm-btn">
            <Printer className="w-4 h-4 me-2" />
            {isArabic ? 'تنزيل PDF' : 'Download PDF'}
          </Button>
          <Button onClick={onClose} variant="outline" data-testid="print-cancel-btn">
            {isArabic ? 'إلغاء' : 'Cancel'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrintOptionsModal;
